import {
  DragDropContext,
  DragDropProvider,
  DragDropState,
  DropTarget,
} from '@wandb/common/containers/DragDropContainer';
import {compact, findIndex, flatten, isEqual} from 'lodash';
import React, {
  CSSProperties,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {Virtuoso} from 'react-virtuoso';
import {Button} from 'semantic-ui-react';

import {usePrevious} from '../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {useRunsWandbConfigQuery} from '../../state/runs/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import * as PanelBankConfigTypes from '../../state/views/panelBankConfig/types';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import * as PanelSettingsTypes from '../../state/views/panelSettings/types';
import {RunHistoryKeyInfo} from '../../types/run';
import {
  getMousePositionMemoized,
  getMousePositionRelativeTo,
} from '../../util/mouse';
import {
  EMPTY_PANEL_BANK_SETTINGS,
  expectedAPIAddedPanelSpecsFromPanelBankConfigs,
  getPanelBankDiff,
  isPanel,
  isPanelBankSection,
  keyInfoToAddedPanels,
  keyInfoToDefaultPanelSpecs,
  panelBankConfigToFoundPanelSpecs,
  panelIsActive,
  searchRegexFromQuery,
  searchRegexMatchesPanel,
} from '../../util/panelbank';
import * as Panels from '../../util/panels';
import {LayedOutPanelWithRef} from '../../util/panels';
import * as RunHelpers from '../../util/runhelpers';
import {EmptySectionMessageNoMetrics} from './EmptySectionMessage';
import {PanelBankConfigWithRefs} from './hooks/usePanelBankConfigWithRefs';
import {PanelBankProps} from './PanelBank';
import PanelBankAddVisButton from './PanelBankAddVisButton';
import {PanelBankSection} from './PanelBankSection';
import {PanelBankConfigState, PanelBankSectionConfig} from './types';

const MARGIN_DRAG_HANDLE_ICON = 12;
const SECTION_TITLE_HEIGHT = 49;

type PanelBankSectionsProps = PanelBankProps & {
  warningVisible?: boolean;
  historyKeyInfo: RunHistoryKeyInfo;
  historyKeyViz: {[key: string]: any};
  panelBankWidth: number;
  panelBankConfigWithRefs: PanelBankConfigWithRefs; // TODO: get this from context
  panelBankSectionsDomRef: RefObject<HTMLDivElement>;
};

// This component renders the Panelbank sections (everything besides search + options bar)
// Most of the PanelBank logic lives here
const PanelBankSections: FC<PanelBankSectionsProps> = props => {
  const {
    initializeNewPanels,
    panelBankDiff,
    addSection,
    deleteSection,
    moveSectionBefore,
    movePanel,
  } = usePanelBankSectionsProps(props);

  const {
    panelBankConfigRef,
    warningVisible,
    historyKeyInfo,
    historyKeyViz,
    readOnly,
    panelSettingsRef,
    panelBankWidth,
    runSetRefs,
    customRunColorsRef,
    singleRun,
    entityName,
    panelBankConfigWithRefs,
    panelBankSectionsDomRef,
  } = props;

  const {debouncedSearchQuery} = usePanelSearchDebouncedContext();

  useEffect(
    () =>
      panelBankSectionsEffect({
        initializeNewPanels,
        panelBankDiff,
      }),
    [
      historyKeyInfo,
      historyKeyViz,
      initializeNewPanels,
      panelBankDiff,
      runSetRefs,
      debouncedSearchQuery,
      panelSettingsRef,
      panelBankConfigRef,
    ]
  );
  const lastShiftKeyRef = useRef(false);

  const {visibleSectionsWithRefs} = usePanelBankSectionList(
    panelBankConfigWithRefs,
    debouncedSearchQuery,
    singleRun,
    historyKeyInfo
  );

  const {defaultMoveToSectionName} = panelBankConfigWithRefs.settings;
  const defaultMoveToSection = panelBankConfigWithRefs.sections.find(s => {
    return s.name === defaultMoveToSectionName;
  });

  // NOTE: this assumes Hidden Panels is the last section
  const hiddenSection = panelBankConfigWithRefs.sections.slice(-1)[0];

  // While dragging sections, all sections are collapsed.
  // This function adds a spacer to account for the height difference.
  const getTopSpacerHeightWhenDraggingSection = useCallback(
    (dragDropContext: DragDropState) => {
      const draggingSectionIndex = findIndex(visibleSectionsWithRefs, s => {
        return s.ref.id === dragDropContext.dragRef?.id;
      });
      const panelBankSectionsElement = panelBankSectionsDomRef.current;
      const mousePosition =
        dragDropContext.mouseDownEvent != null &&
        panelBankSectionsElement != null
          ? getMousePositionRelativeTo(
              dragDropContext.mouseDownEvent,
              panelBankSectionsElement
            )
          : null;

      const result =
        draggingSectionIndex > 0 && mousePosition != null
          ? mousePosition.client.y -
            draggingSectionIndex * SECTION_TITLE_HEIGHT -
            mousePosition.offset.y -
            MARGIN_DRAG_HANDLE_ICON
          : 0;

      return result;
    },
    [visibleSectionsWithRefs, panelBankSectionsDomRef]
  );

  if (
    panelBankConfigWithRefs.state !== PanelBankConfigState.Ready &&
    visibleSectionsWithRefs.every(s => s.activePanelRefs.length === 0) &&
    historyKeyInfo.lastStep != null &&
    historyKeyInfo.lastStep < 0
  ) {
    return (
      <>
        <div className="no-metrics-logged">
          <EmptySectionMessageNoMetrics />
        </div>
        <div className="panel-bank__divider" />
        <div style={{padding: 16}}>
          <Button
            data-test="panelbank-add-section-button"
            size="tiny"
            content={'Add section'}
            onClick={() => {
              addSection(hiddenSection.ref);
            }}
          />
        </div>
      </>
    );
  }

  const getTopSpacerHeightWhenDraggingPanel = (
    dragDropContext: DragDropState
  ) => {
    const sectionIndexOnShift = findIndex(visibleSectionsWithRefs, s => {
      return s.ref.id === dragDropContext.dropRefOnShift?.id;
    });
    const mouseYOnShift = dragDropContext.mouseEventOnShift?.pageY!;
    const result =
      sectionIndexOnShift > -1
        ? mouseYOnShift -
          72 - // panelbank top padding
          52 - // nav bar height
          (singleRun ? 44 : 0) - // run workspace run name header
          (warningVisible ? 52 : 0) - // warning message height (e.g. slow-warning)
          (sectionIndexOnShift + 0.5) * SECTION_TITLE_HEIGHT
        : 0;
    return result;
  };

  const scrollToElementOnShiftRelease = (dragDropContext: DragDropState) => {
    if (
      dragDropContext.elementOnShiftRelease != null &&
      dragDropContext.mouseEventOnShiftRelease != null
    ) {
      window.scrollTo({
        top:
          (dragDropContext.elementOnShiftRelease as any).offsetTop +
          72 +
          52 -
          getMousePositionMemoized(dragDropContext.mouseEventOnShiftRelease)
            .client.y +
          25,
      });
    }
  };

  return (
    <>
      <DragDropProvider>
        <DragDropContext.Consumer>
          {dragDropContext => {
            const isDraggingPanel =
              dragDropContext.dragRef != null &&
              isPanel(dragDropContext.dragRef);
            const isDraggingSection =
              dragDropContext.dragRef != null &&
              isPanelBankSection(dragDropContext.dragRef);

            if (!dragDropContext.shiftKey && lastShiftKeyRef.current) {
              setTimeout(() => {
                scrollToElementOnShiftRelease(dragDropContext);
              });
            }
            lastShiftKeyRef.current = dragDropContext.shiftKey;

            const shouldRenderPanelBankSpacer =
              ((isDraggingPanel && dragDropContext.shiftKey) ||
                isDraggingSection) &&
              dragDropContext.mouseDownEvent != null;

            return (
              <>
                {shouldRenderPanelBankSpacer && (
                  <PanelBankSpacer
                    dropTargetSectionRef={visibleSectionsWithRefs[0].ref}
                    onDrop={() => {
                      if (isDraggingSection) {
                        moveSectionBefore(
                          dragDropContext.dragRef as PanelBankSectionConfigTypes.Ref,
                          visibleSectionsWithRefs[0].ref
                        );
                      }
                    }}
                    style={{
                      height: isDraggingSection
                        ? getTopSpacerHeightWhenDraggingSection(dragDropContext)
                        : getTopSpacerHeightWhenDraggingPanel(dragDropContext),
                    }}
                  />
                )}
                <Virtuoso
                  style={{flexGrow: 1}}
                  overscan={3000}
                  data={visibleSectionsWithRefs}
                  useWindowScroll
                  components={{
                    Footer: () => {
                      return (
                        <div
                          style={{
                            padding: '1rem 2rem',
                            display: isDraggingSection ? 'none' : undefined,
                          }}>
                          <Button
                            data-test="panelbank-add-section-button"
                            size="tiny"
                            content={'Add section'}
                            onClick={() => addSection(hiddenSection.ref)}
                          />
                        </div>
                      );
                    },
                  }}
                  itemContent={(idx, renderSectionWithRefs) => {
                    const sectionRef = renderSectionWithRefs.ref;
                    return (
                      <PanelBankSection
                        key={sectionRef.id}
                        panelBankWidth={panelBankWidth}
                        readOnly={readOnly}
                        workspacePanelSettingsRef={panelSettingsRef}
                        panelBankConfigRef={panelBankConfigRef}
                        historyKeyInfo={historyKeyInfo}
                        panelBankSectionConfigRef={sectionRef}
                        customRunColorsRef={customRunColorsRef}
                        runSetRefs={runSetRefs}
                        //// TODO: probably move to context, but remember report grids
                        activePanelRefs={renderSectionWithRefs.activePanelRefs}
                        inactivePanelRefs={
                          renderSectionWithRefs.inactivePanelRefs
                        }
                        panelSearchResults={
                          renderSectionWithRefs.panelSearchResults
                        }
                        panelSearchMatchCount={
                          renderSectionWithRefs.panelSearchMatchCount
                        }
                        isSectionNameSearchMatch={
                          renderSectionWithRefs.isSectionNameSearchMatch
                        }
                        /////
                        moveSectionBefore={moveSectionBefore}
                        addSectionAbove={() => {
                          addSection(sectionRef);
                        }}
                        addSectionBelow={() => {
                          addSection(sectionRef, {addAfter: true});
                        }}
                        deleteSection={() => deleteSection(sectionRef)}
                        // deletePanel={panelRef =>
                        //   deletePanel(panelRef, sectionRef)
                        // }
                        defaultMoveToSectionRef={
                          defaultMoveToSection
                            ? defaultMoveToSection.ref
                            : undefined
                        }
                        movePanel={movePanel}
                        addVisButton={
                          <PanelBankAddVisButton
                            singleRun={singleRun}
                            entityName={entityName}
                            customRunColorsRef={customRunColorsRef}
                            runSetRefs={runSetRefs}
                            panelSettingsRef={panelSettingsRef}
                            panelBankSectionConfigRef={sectionRef}
                          />
                        }
                      />
                    );
                  }}
                />
                {shouldRenderPanelBankSpacer && (
                  <PanelBankSpacer
                    dropTargetSectionRef={
                      visibleSectionsWithRefs.slice(-1)[0].ref
                    }
                    onDrop={() => {
                      moveSectionBefore(
                        dragDropContext.dragRef as PanelBankSectionConfigTypes.Ref
                      );
                    }}
                    style={{
                      height: '100vh',
                    }}
                  />
                )}
              </>
            );
          }}
        </DragDropContext.Consumer>
      </DragDropProvider>
    </>
  );
};

export type SectionWithRef = Omit<PanelBankSectionConfig, 'panels'> & {
  ref: PanelBankSectionConfigTypes.Ref;
  panels: LayedOutPanelWithRef[];
  localPanelSettingsRef: PanelSettingsTypes.Ref;
};

export type SectionMetadata = {
  // These are the panels that should be rendered. Note they may be a subset
  // of the panels present in config, due to data availability.
  // It's important to use the config's panels in update logic.
  activePanelRefs: ReadonlyArray<PanelTypes.Ref>;
  // These are panels that will not be rendered.  They were filtered out
  // by hasDataForPanel
  inactivePanelRefs: ReadonlyArray<PanelTypes.Ref>;
  // Map of panels that match the current searchRegex
  panelSearchResults?: Map<PanelTypes.Ref, boolean>;
  // Number of search hits in the section
  panelSearchMatchCount?: number;
  // Does the section name match the search query?
  isSectionNameSearchMatch?: boolean;
};

export type SectionWithMetadata = SectionWithRef & SectionMetadata;

const usePanelBankSectionList = (
  panelBankConfigWithRefs: PanelBankConfigWithRefs,
  debouncedSearchQuery: string,
  singleRun: boolean | undefined,
  historyKeyInfo: RunHistoryKeyInfo
) => {
  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo);

  const {showEmptySections} =
    panelBankConfigWithRefs.settings || EMPTY_PANEL_BANK_SETTINGS;
  // Filter out panels that don't have data, and panels that don't match the search query
  // Also adds SectionMetadata to the sections

  // TODO(perf): this is a good candidate for perf testing.
  // do we really need to loop through every panel, before filtering sections?
  // do we need to loop through every section? or just the visible ones?
  // break the section+panel loops early when we have enough to display 1 page
  const visibleSectionsWithRefs = useMemo(() => {
    // TODO: move searchRegex to search context
    const searchRegex = searchRegexFromQuery(debouncedSearchQuery);
    const isSearching = !!searchRegex;

    return panelBankConfigWithRefs.sections.reduce(
      (filteredSections: SectionWithMetadata[], section) => {
        // activePanelRefs are the panels that may get rendered.
        // NOTE: this includes panels that match and don't match the panel search regex. we want to keep them in the same array so we can maintain the panel order
        // for flow sections, activePanelRefs = panels with data
        // for grid sections, activePanelRefs = all panels (we show placeholders for panels with no data)
        const activePanelRefs: PanelTypes.Ref[] = [];

        // inactivePanelRefs are panels with no data (or histograms in multi-run workspaces, see isPanelActive
        const inactivePanelRefs: PanelTypes.Ref[] = [];

        // panelSearchResults is a map of search results. keys are panelRefs, values are boolean
        const panelSearchResults = new Map<PanelTypes.Ref, boolean>();

        let panelSearchMatchCount = 0;
        section.panels.forEach(panel => {
          const isPanelActive = panelIsActive({
            section,
            panel,
            singleRun,
            historyKeyInfo,
            keyTypes,
          });

          // filter out panels that don't have data
          if (!isPanelActive) {
            inactivePanelRefs.push(panel.ref);
            return;
          }

          activePanelRefs.push(panel.ref);

          // If there's a searchRegex and the panel matches, set search result value to true
          if (
            searchRegex != null &&
            searchRegexMatchesPanel(searchRegex, panel)
          ) {
            panelSearchResults.set(panel.ref, true);
            panelSearchMatchCount++;
            return;
          }

          panelSearchResults.set(panel.ref, false);
          return;
        });

        // While searching, we also want to display sections with names that match the search
        const isSectionNameSearchMatch =
          searchRegex?.test(section.name) ?? false;

        const isSectionVisible =
          // showEmptySections is a user workspace setting
          showEmptySections ||
          // show section if not searching and it has active panels
          (!isSearching && activePanelRefs.length > 0) ||
          // show section if searching and section name or any panels match the search
          (isSearching &&
            (isSectionNameSearchMatch || panelSearchMatchCount > 0)) ||
          // Edge case: sections with no panel refs (active or inactive) are newly-created by the user.
          // We want to show these sections so panels can be dragged into them
          activePanelRefs.length + inactivePanelRefs.length === 0;

        if (!isSectionVisible) {
          return filteredSections;
        }

        filteredSections.push({
          ...section,
          activePanelRefs,
          inactivePanelRefs,
          panelSearchResults,
          panelSearchMatchCount,
          isSectionNameSearchMatch,
        });

        return filteredSections;
      },
      []
    );
  }, [
    debouncedSearchQuery,
    historyKeyInfo,
    keyTypes,
    panelBankConfigWithRefs.sections,
    showEmptySections,
    singleRun,
  ]);

  return {
    visibleSectionsWithRefs,
  };
};

export default PanelBankSections;

// All sections collapse to just headers when you're dragging.
// This spacer preserves the height and provides a drop target above+below the sections.
const PanelBankSpacer = (props: {
  dropTargetSectionRef: PanelBankSectionConfigTypes.Ref;
  style: CSSProperties;
  onDrop(): void;
}) => {
  return (
    <DropTarget
      isValidDropTarget={({dragRef}) => {
        return isPanelBankSection(dragRef);
      }}
      partRef={props.dropTargetSectionRef}
      onDrop={({dragRef}) => {
        if (dragRef && !isEqual(dragRef, props.dropTargetSectionRef)) {
          props.onDrop();
        }
      }}
      style={props.style}
    />
  );
};

function usePanelBankConfigActions(
  panelBankConfigRef: PanelBankConfigTypes.Ref
) {
  const initializeNewPanels = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.initializeNewPanels
  );
  const addSection = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.addSection
  );
  const deleteSection = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.deleteSection
  );
  const moveSectionBefore = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.moveSectionBefore
  );
  const movePanel = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.movePanel
  );

  return {
    initializeNewPanels,
    addSection,
    deleteSection,
    moveSectionBefore,
    movePanel,
  };
}

function usePanelBankSectionsProps(props: PanelBankSectionsProps) {
  const panelBankConfigActions = usePanelBankConfigActions(
    props.panelBankConfigRef
  );
  const keysInPanel: string[] = compact(
    flatten(
      props.panelBankConfigWithRefs.sections.map(section =>
        section.panels.map(panel => Panels.getKey(panel))
      )
    )
  );
  const prevPropsKeys = usePrevious(keysInPanel);
  const wandbConfigQueryResult = useRunsWandbConfigQuery(props.runSetRefs);

  if (
    !wandbConfigQueryResult ||
    wandbConfigQueryResult.loading ||
    wandbConfigQueryResult.error
  ) {
    return {
      ...panelBankConfigActions,
      panelBankDiff: {},
    };
  }
  const wbConfig = wandbConfigQueryResult.wandbConfig;

  const expectedPanelSpecs = keyInfoToDefaultPanelSpecs(
    props.historyKeyInfo,
    props.historyKeyViz,
    !!props.singleRun,
    props.panelBankConfigWithRefs.settings,
    wbConfig
  );

  const addedPanelSpecs = keyInfoToAddedPanels(
    props.historyKeyViz,
    props.panelBankConfigWithRefs.settings.autoOrganizePrefix
  );

  const foundPanelSpecs = panelBankConfigToFoundPanelSpecs(
    props.panelBankConfigWithRefs
  );

  const expectedAddedPanelConfigsWithRefs =
    expectedAPIAddedPanelSpecsFromPanelBankConfigs(
      props.panelBankConfigWithRefs,
      addedPanelSpecs
    );

  const panelBankDiff = getPanelBankDiff(
    expectedPanelSpecs,
    expectedAddedPanelConfigsWithRefs,
    addedPanelSpecs,
    foundPanelSpecs,
    prevPropsKeys || [],
    props.panelBankConfigWithRefs.state
  );

  return {
    ...panelBankConfigActions,
    panelBankDiff,
  };
}

function panelBankSectionsEffect(
  effectProps: Pick<
    ReturnType<typeof usePanelBankSectionsProps>,
    'initializeNewPanels' | 'panelBankDiff'
  >
) {
  const {initializeNewPanels, panelBankDiff} = effectProps;

  // Short-circuit if there are no new keys
  if (Object.keys(panelBankDiff).length === 0) {
    return;
  }
  initializeNewPanels(panelBankDiff);
}
